<template>
  <div :class="$style.base">
    <Bar v-bind="{ chartOptions, chartData }" />
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import { useI18n } from 'vue-i18n';
import dayjs from '@/dayjs';
import { useThemeStore } from '@/stores/theme';
import { useColors } from './colors';

const { t } = useI18n();
const colors = useColors();

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const props = defineProps<{
  data: {
    startAt: string;
    endAt: string;
    data: {
      scheduledHours: number;
      availableHours: number;
    };
  }[];
}>();

const labels = computed(() =>
  props.data.map((period) => dayjs(period.startAt).format('DD-MM')),
);

const chartData = {
  labels: labels.value,
  datasets: [
    {
      label: t('insights.time_booked'),
      data: props.data.map((period) => period.data.scheduledHours),
      backgroundColor: props.data.map((_, index) =>
        index === props.data.length - 1 ? colors.booked : colors.bookedLight,
      ),
      stack: 'weekly',
    },
    {
      label: t('insights.time_available'),
      data: props.data.map((period) => period.data.availableHours),
      backgroundColor: props.data.map((_, index) =>
        index === props.data.length - 1
          ? colors.available
          : colors.availableLight,
      ),
      stack: 'weekly',
    },
  ],
};

const { theme } = useThemeStore();
const textColor = theme === 'DARK' ? '#ffffff' : '#555555';
const gridColor = theme === 'DARK' ? '#444444' : '#dddddd';

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: false,
  },
  scales: {
    x: {
      ticks: {
        font: labels.value.map((_, index) => ({
          ...ChartJS.defaults.font,
          weight: index === labels.value.length - 1 ? 'bold' : 'normal',
        })),
        color: textColor,
      },
      grid: {
        color: gridColor,
      },
    },
    y: {
      ticks: {
        color: textColor,
      },
      grid: {
        color: gridColor,
      },
    },
  },
};
</script>

<style
  lang="scss"
  module
>
.base {
  height: 300px;
  position: relative;

  & > * {
    height: 100%;
  }
}
</style>
